import { Formik, Field, Form } from 'formik';
import { useReducer } from 'react';
import { Plus } from 'lucide-react';

import { User } from '@/portainer/users/types';
import { notifySuccess } from '@/portainer/services/notifications';
import { usePublicSettings } from '@/react/portainer/settings/queries';
import { useHasEnabledAddon } from '@/react/portainer/addons/queries/useHasEnabledAddon';
import { useAddons } from '@/react/portainer/addons/queries/useAddons';
import { useUpdateTeamAddonAccessMutation } from '@/react/portainer/addons/queries/useUpdateTeamAddonAccessMutation';

import { FormControl } from '@@/form-components/FormControl';
import { Widget } from '@@/Widget';
import { Input } from '@@/form-components/Input';
import { UsersSelector } from '@@/UsersSelector';
import { LoadingButton } from '@@/buttons/LoadingButton';
import { TextTip } from '@@/Tip/TextTip';
import { SwitchField } from '@@/form-components/SwitchField';
import { PortainerSelect } from '@@/form-components/PortainerSelect';

import { Team } from '../../types';
import { useAddTeamMutation } from '../../queries/useAddTeamMutation';
import { DENY_PORTAINER_ACCESS_TOOLTIP } from '../../denyPortainerAccess';

import { FormValues } from './types';
import { validationSchema } from './CreateTeamForm.validation';

interface Props {
  users: User[];
  teams: Team[];
}

export function CreateTeamForm({ users, teams }: Props) {
  const addTeamMutation = useAddTeamMutation();
  const updateTeamAddonAccessMutation = useUpdateTeamAddonAccessMutation();
  const [formKey, incFormKey] = useReducer((state: number) => state + 1, 0);
  const teamSyncQuery = usePublicSettings<boolean>({
    select: (settings) => settings.TeamSync,
  });
  const hasEnabledAddon = useHasEnabledAddon();
  const addonsQuery = useAddons({
    select: (data) => data?.addons?.filter((addon) => addon.enabled) ?? [],
  });
  const addonOptions = (addonsQuery.data ?? []).map((addon) => ({
    label: addon.displayName ?? addon.id ?? '',
    value: addon.id ?? '',
  }));

  const initialValues: FormValues = {
    name: '',
    leaders: [],
    denyPortainerAccess: false,
    addons: [],
  };

  return (
    <div className="row">
      <div className="col-lg-12 col-md-12 col-xs-12">
        <Widget>
          <Widget.Title
            icon={Plus}
            title="Add a new team"
            className="vertical-center"
          />
          <Widget.Body>
            <Formik
              initialValues={initialValues}
              validationSchema={() => validationSchema(teams)}
              onSubmit={handleAddTeamClick}
              validateOnMount
              key={formKey}
            >
              {({
                values,
                errors,
                handleSubmit,
                setFieldValue,
                isSubmitting,
                isValid,
              }) => (
                <Form
                  className="form-horizontal"
                  onSubmit={handleSubmit}
                  noValidate
                >
                  <FormControl
                    inputId="team_name"
                    label="Name"
                    errors={errors.name}
                    required
                  >
                    <Field
                      as={Input}
                      name="name"
                      id="team_name"
                      required
                      placeholder="e.g. development"
                      data-cy="team-teamNameInput"
                    />
                  </FormControl>

                  {users.length > 0 && (
                    <FormControl
                      inputId="users-input"
                      label="Select team leader(s)"
                      tooltip="You can assign one or more leaders to this team. Team leaders can manage their teams users and resources."
                      errors={errors.leaders}
                    >
                      <UsersSelector
                        value={values.leaders}
                        onChange={(leaders) =>
                          setFieldValue('leaders', leaders)
                        }
                        users={users}
                        dataCy="team-teamLeaderSelect"
                        inputId="users-input"
                        placeholder="Select one or more team leaders"
                        disabled={teamSyncQuery.data}
                      />
                    </FormControl>
                  )}

                  {teamSyncQuery.data && (
                    <div className="form-group">
                      <div className="col-sm-12">
                        <TextTip color="orange">
                          The team leader feature is disabled as external
                          authentication is currently enabled with team sync.
                        </TextTip>
                      </div>
                    </div>
                  )}

                  {hasEnabledAddon && (
                    <div className="form-group">
                      <div className="col-sm-12">
                        <SwitchField
                          label="Restrict to add-ons only"
                          tooltip={DENY_PORTAINER_ACCESS_TOOLTIP}
                          checked={values.denyPortainerAccess}
                          onChange={(value) =>
                            setFieldValue('denyPortainerAccess', value)
                          }
                          data-cy="team-denyPortainerAccessToggle"
                        />
                      </div>
                    </div>
                  )}

                  {addonOptions.length > 0 && (
                    <FormControl
                      inputId="addons-input"
                      label="Grant access to add-ons"
                      tooltip="Give this team access to one or more add-ons."
                      errors={errors.addons}
                    >
                      <PortainerSelect<string>
                        isMulti
                        value={values.addons}
                        onChange={(value) => setFieldValue('addons', value)}
                        options={addonOptions}
                        inputId="addons-input"
                        data-cy="team-teamAddonsSelect"
                        placeholder="Select one or more add-ons"
                        isLoading={addonsQuery.isLoading}
                      />
                    </FormControl>
                  )}

                  <div className="form-group">
                    <div className="col-sm-12">
                      <LoadingButton
                        disabled={!isValid}
                        data-cy="team-createTeamButton"
                        isLoading={isSubmitting || addTeamMutation.isLoading}
                        loadingText="Creating team..."
                        icon={Plus}
                        className="!ml-0"
                      >
                        Create team
                      </LoadingButton>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </Widget.Body>
        </Widget>
      </div>
    </div>
  );

  async function handleAddTeamClick(values: FormValues) {
    addTeamMutation.mutate(values, {
      async onSuccess(team) {
        if (values.addons.length > 0) {
          try {
            await updateTeamAddonAccessMutation.mutateAsync({
              addonIds: values.addons,
              teamId: team.Id,
            });
          } catch {
            // Team creation still succeeded; the mutation's own withError
            // already notified the user of the grant failure.
          }
        }
        incFormKey();
        notifySuccess('Team successfully added', '');
      },
    });
  }
}
